import { SegmentedControl, type SegmentedControlProps } from '@mantine/core';
import { GenderType } from '@scoring/types';
import { IconCircleOff, IconGenderMale, IconUser, IconVenus } from '@tabler/icons-react';
import React from 'react';

export interface GenderInputProps extends Omit<SegmentedControlProps, 'data'> {
    /** Append Mixed after the other values, even when genderValues is set. */
    includeMixed?: boolean;
    /** The gender values to display. Defaults to Male and Female. */
    genderValues?: (GenderType | 'Mixed' | 'None')[];
    label?: string;
    error?: React.ReactNode;
    onChange?: (value: string) => void;
}

const ICON_BOX = 24;

const genderIcon = (icon: React.ReactNode) => (
    <span
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: ICON_BOX,
            height: ICON_BOX,
        }}
    >
        {icon}
    </span>
);

export const GenderInput = ({
    includeMixed = false,
    genderValues,
    size = 'xs',
    label,
    error,
    onChange,
    ...props
}: GenderInputProps) => {
    const base = genderValues ?? [GenderType.Male, GenderType.Female];
    const values = includeMixed && !base.includes('Mixed') ? [...base, 'Mixed' as const] : base;

    const data = values.map((gender) => {
        switch (gender) {
            case GenderType.Male:
                return {
                    value: GenderType.Male,
                    label: genderIcon(<IconGenderMale />),
                };
            case GenderType.Female:
                return {
                    value: GenderType.Female,
                    label: genderIcon(<IconVenus />),
                };
            case 'Mixed':
                return {
                    value: 'Mixed',
                    label: genderIcon(<IconUser color="#800080" />),
                };
            case 'None':
                return {
                    value: 'None',
                    label: genderIcon(<IconCircleOff size={16} />),
                };
            default:
                return {
                    value: gender,
                    label: gender,
                };
        }
    });

    return (
        <SegmentedControl
            bg={'light-dark(var(--mantine-color-white), var(--mantine-color-dark-6))'}
            bdrs={4}
            bd={'1px solid light-dark(var(--mantine-color-gray-4), var(--mantine-color-dark-4))'}
            {...props}
            data={data}
            p={0}
            m={0}
            fullWidth
            size={size}
            radius="xs"
            transitionDuration={250}
            transitionTimingFunction="linear"
            onChange={onChange}
        />
    );
};

export default GenderInput;
